import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { startOfDay, subDays, startOfWeek } from 'date-fns';
import { complianceEngineService } from '@/services';
import { CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';

interface DashboardAIComplianceCardProps {
  companyId: string;
}

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

function computeSinceDate(period: string): string {
  const now = new Date();
  switch (period) {
    case 'this_week':
      return startOfWeek(now, { weekStartsOn: 1 }).toISOString();
    case 'last_30_days':
      return subDays(now, 30).toISOString();
    case 'since_yesterday':
    default:
      return startOfDay(subDays(now, 1)).toISOString();
  }
}

export function DashboardAIComplianceCard({ companyId }: DashboardAIComplianceCardProps) {
  const navigate = useNavigate();
  const [period, setPeriod] = useState<string>('last_30_days');
  const sinceDate = computeSinceDate(period);

  const { data: rows, isLoading } = useQuery({
    queryKey: ['dashboard-ai-compliance', companyId, period],
    queryFn: async () => {
      const result = await complianceEngineService.getEngineDashboardSummary(companyId, sinceDate);
      if (result.error) throw result.error;
      return result.data;
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!companyId,
  });

  return (
    <>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <CardTitle>New Non-compliant Invoices to Review</CardTitle>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[160px] h-8 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="since_yesterday">Since yesterday</SelectItem>
              <SelectItem value="this_week">This week</SelectItem>
              <SelectItem value="last_30_days">Last 30 Days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent className="p-0 flex-1 overflow-auto">
        {isLoading ? (
          <div className="p-4 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </div>
        ) : rows && rows.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Invoice</TableHead>
                <TableHead>Supplier</TableHead>
                <TableHead className="text-right">Issues</TableHead>
                <TableHead className="text-right">Overcharge</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow
                  key={row.invoice_id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => navigate(`/invoices/${row.invoice_id}`)}
                >
                  <TableCell className="text-sm">
                    {row.invoice_number || '—'}
                  </TableCell>
                  <TableCell className="text-sm">
                    {row.supplier_name}
                  </TableCell>
                  <TableCell className="text-sm text-right">
                    {row.failed_count}
                  </TableCell>
                  <TableCell className="text-sm text-right text-destructive">
                    {row.total_overcharge > 0
                      ? currencyFormatter.format(row.total_overcharge)
                      : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="h-full flex items-center justify-center text-muted-foreground p-6">
            No AI compliance issues found
          </div>
        )}
      </CardContent>
    </>
  );
}
